<template>
  <div class="card background-white mt-4" qid="tree-view-container">
    <div class="card-body">
      <div class="mb-3 row">
        <div class="col-md-2 pt-2">
          <span>{{ $t('tree_view.process_group_filter') }}</span>
        </div>
        <div class="col-md-10">
          <multiselect
              v-model="process_group"
              :options="activeProcessGroups"
              :placeholder="$t('processes.create.process_group_placeholder')"
              @select="debounceInput"
              @remove="debounceInput"
              track-by="id"
              :select-label="$t('system.dropdown_select_label')"
              :selected-label="$t('system.dropdown_selected_label')"
              :deselect-label="$t('system.dropdown_deselect_label')"
              label="name"
              class="mw-300"
              qid="filter-p-form-process_group"
          ></multiselect>
        </div>
      </div>

      <table class="table table-responsive-sm">
        <tbody>
          <tr>
            <th>
              <span>{{ $t('table.title') }}</span>
            </th>
            <th>
              {{ $t('activities.processing_role.title') }}
            </th>
            <th class="text-right">
              <span class="pr-5">
                {{ $t('validations.label') }}
              </span>
              <div class="pl-2 tree--user-container">
                {{ $t('table.last_updated') }}
              </div>
              <div class="tree--navigate-header mr-1"></div>
            </th>
          </tr>
          <template v-for="(object, index) in tableItems">
            <tr>
              <td class="text-left">
                <span>
                  <span @click="loadChildren(object, index)" class="pr-3">
                    <span class="expand-button cursor-pointer">
                      <font-awesome-icon icon="plus-circle" v-if="!object.expanded" />
                    </span>
                    <span class="collapse-button cursor-pointer">
                      <font-awesome-icon icon="minus-circle" v-if="object.expanded" />
                    </span>
                  </span>
                  <span v-b-tooltip.hover :title="$t('processes.log.title') | capitalize" class="pr-2">
                    <font-awesome-icon icon="cube" />
                  </span>
                  <span v-if="object.name">
                    <router-link
                        :to="{ name: 'processes.view', params: { slug: object.slug }}"
                        qid="view-processes-link"
                        v-if="isGranted([permissions.PROCESS_VIEW, permissions.PROCESS_VIEW_OWN], object)"
                    >
                       {{object.name}}
                    </router-link>
                    <span v-if="!isGranted([permissions.PROCESS_VIEW, permissions.PROCESS_VIEW_OWN], object)">
                      {{ object.name }}
                    </span>
                  </span>
                </span>
              </td>
              <td>
                <span></span>
              </td>
              <td>
                <div class="text-right">
                  <span class="pl-5 float-right tree--navigate-header">
                    <span>&nbsp;</span>
                    <router-link
                        :to="{ name: 'processes.view', params: { slug: object.slug }}"
                        qid="view-processes-link"
                        v-if="isGranted([permissions.PROCESS_VIEW, permissions.PROCESS_VIEW_OWN], object)"
                        class="btn btn-view"
                    >
                      <font-awesome-icon icon="eye" />
                    </router-link>
                  </span>
                  <span class="pr-6">
                    <span v-if="object.validation == true">
                      <font-awesome-icon icon="check-circle" class="icon-success"/>
                    </span>
                    <span v-if="object.validation == false">
                      <font-awesome-icon icon="times-circle" class="icon-danger"/>
                    </span>
                  </span>
                  <div v-if="object.updated_by" class="pl-2 tree--user-container">
                    <Avatar :object="object.updated_by" class="mr-2"></Avatar>
                    {{ object.updated_at | formatDate }}
                  </div>
                </div>
              </td>
            </tr>

            <template v-for="(child, childIndex) in object.children" v-if="object.children">
              <tr class="pl-4">
                <td class="text-left tree--children-padding">
                  <span>
                    <span @click="loadGrandChildren(object, child, childIndex, index)" class="pr-3">
                      <span class="expand-button cursor-pointer">
                        <font-awesome-icon icon="plus-circle" v-if="!child.expanded" />
                      </span>
                      <span class="collapse-button cursor-pointer">
                        <font-awesome-icon icon="minus-circle" v-if="child.expanded" />
                      </span>
                    </span>
                    <span v-b-tooltip.hover :title="$t('activities.log.title') | capitalize" class="pr-2">
                      <font-awesome-icon icon="cubes" />
                    </span>
                    <span v-if="child.name">
                      <router-link
                          :to="{ name: 'activities.view', params: { slug: child.slug }}"
                          qid="view-activities-link"
                          v-if="isGranted([permissions.ACTIVITY_VIEW, permissions.ACTIVITY_VIEW_OWN], child)"
                      >
                        {{ child.name }}
                      </router-link>
                      <span v-if="!isGranted([permissions.ACTIVITY_VIEW, permissions.ACTIVITY_VIEW_OWN], child)">
                        {{ child.name }}
                      </span>
                    </span>
                  </span>
                </td>
                <td>
                  <span v-if="child.gdpr && child.gdpr.processing_role">
                    {{ child.gdpr.processing_role }}
                  </span>
                </td>
                <td>
                  <div class="text-right">
                    <span class="pl-5 float-right tree--navigate-header">
                      <span>&nbsp;</span>
                      <router-link
                          :to="{ name: 'activities.view', params: { slug: child.slug }}"
                          qid="view-activities-link"
                          v-if="isGranted([permissions.ACTIVITY_VIEW, permissions.ACTIVITY_VIEW_OWN], child)"
                          class="btn btn-view"
                      >
                        <font-awesome-icon icon="eye" />
                      </router-link>
                    </span>
                    <span class="pr-6">
                      <span v-if="child.validation == true">
                        <font-awesome-icon icon="check-circle" class="icon-success"/>
                      </span>
                      <span v-if="child.validation == false">
                        <font-awesome-icon icon="times-circle" class="icon-danger"/>
                      </span>
                    </span>
                    <span v-if="child.updated_by" class="pl-2 tree--user-container">
                      <Avatar :object="child.updated_by" class="mr-2"></Avatar>
                      {{ child.updated_at | formatDate }}
                    </span>
                  </div>
                </td>
              </tr>
              <tr v-for="(grandChild, grandChildIndex) in child.children" v-if="child.children">
                <td class="text-left tree--grandchildren-padding">
                  <span>
                    <span v-if="grandChild.name">
                      <span v-b-tooltip.hover :title="$t('systems.log.title') | capitalize" class="pr-2">
                        <font-awesome-icon icon="desktop" />
                      </span>
                      <router-link
                          :to="{ name: 'systems.view', params: { slug: grandChild.slug }}"
                          qid="view-systems-link"
                          v-if="isGranted([permissions.SYSTEM_VIEW, permissions.SYSTEM_VIEW_OWN], grandChild)"
                      >
                        {{ grandChild.name }}
                      </router-link>
                      <span v-if="!isGranted([permissions.SYSTEM_VIEW, permissions.SYSTEM_VIEW_OWN], grandChild)">
                        {{ grandChild.name }}
                      </span>
                    </span>
                  </span>
                </td>
                <td>
                  <span></span>
                </td>
                <td>
                  <div class="text-right">
                    <span class="pl-5 float-right tree--navigate-header">
                      <span>&nbsp;</span>
                      <router-link
                          :to="{ name: 'systems.view', params: { slug: grandChild.slug }}"
                          qid="view-systems-link"
                          v-if="isGranted([permissions.SYSTEM_VIEW, permissions.SYSTEM_VIEW_OWN], grandChild)"
                          class="btn btn-view"
                      >
                        <font-awesome-icon icon="eye" />
                      </router-link>
                    </span>
                    <span class="pr-6">
                      <span v-if="grandChild.validation == true">
                        <font-awesome-icon icon="check-circle" class="icon-success"/>
                      </span>
                      <span v-if="grandChild.validation == false">
                        <font-awesome-icon icon="times-circle" class="icon-danger"/>
                      </span>
                    </span>
                    <span v-if="grandChild.updated_by" class="pl-2 tree--user-container">
                      <Avatar :object="grandChild.updated_by" class="mr-2"></Avatar>
                      {{ grandChild.updated_at | formatDate }}
                    </span>
                  </div>
                </td>
              </tr>
            </template>
            <tr v-if="object.showMore && !childrenLoading">
              <td colspan="10">
                <div class="text-center">
                  <a @click="loadChildren(object, index)">{{ $t('system.show_more') }}</a>
                </div>
              </td>
            </tr>
          </template>
          <tr v-if="showMore && !loading">
            <td colspan="10">
              <div class="text-center">
                <a @click="loadContent()">{{ $t('system.show_more') }}</a>
              </div>
            </td>
          </tr>
          <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
            <td colspan="10">
              <h5 class="mt-3">
                {{ $t('system.no_results') }}
              </h5>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import {config} from '@/config.js'
  import Avatar from '@/components/Avatar.vue';

  export default {
    name: 'HomepageTreeView',
    props: {
    },
    data: function() {
      return {
        tableItems: [],
        childrenTableItems: [],
        loading: true,
        showMore: true,
        childrenLoading: true,
        page: 1,
        activeProcessGroups: [],
        process_group: []
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      }
    },
    watch: {
      getClient: function (value) {
        if (value) {
          this.tableItems = []
          this.loadContent(true);
          this.loadActiveProcessGroups()
        }
      }
    },
    components: {
      Avatar
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadContent(true)
      }, 300),
      loadContent(reset) {
        if (reset) {
          this.tableItems = []
          this.page = 1;
          this.showMore = true;
        }
        let self = this;
        piincHttp.get('processes', { params:
          {
            client: this.getClient.slug,
            page: this.page,
            with_validation: 1,
            sort: 'name',
            order: 'asc',
            process_group: this.process_group ? this.process_group.id : undefined
          }
        }).then(function(response) {
          if (response.data.items && (response.data.items.length < 10 || (response.data.items.length == 10 && response.data.total_count == 10 * self.page))) {
            self.showMore = false;
          }

          self.page = self.page + 1;
          self.loading = false;
          self.tableItems.push(...response.data.items);
        }, function() {});
      },
      loadChildren(object, index) {
        if (!object.children) {
          object.children = []
        }
        let self = this;
        this.childrenLoading = true;
        object.expanded = object.expanded ? false : true;
        object.page = 1;

        if (object.expanded) {
          piincHttp.get('processes/' + object.slug + '/activities', { params:
            {
              page: object.page,
              with_validation: 1,
              sort: 'name',
              order: 'asc'
            }
          }).then(function(response) {
            object.children.push(...response.data);
            object.page = object.page + 1;
            self.$set(self.tableItems, index, object)
            self.childrenLoading = false;
          }, function() {});
        } else {
          object.children = []
          self.$set(self.tableItems, index, object)
        }
      },
      loadGrandChildren(object, child, childIndex, index) {
        child.expanded = child.expanded ? false : true;
        let self = this;
        if (child.expanded) {
          piincHttp.get('activities/' + child.slug + '/systems', { params:
            {
              page: this.page,
              with_validation: 1,
              sort: 'name',
              order: 'asc'
            }
          }).then(function(response) {
            child.children = [];
            _.forEach(response.data, function(item) {
              child.children.push(item.system)
            })

            self.$set(self.tableItems, index, object)
            self.childrenLoading = false;
          }, function() {});
        } else {
          child.children = []
          self.$set(self.tableItems, index, object)
        }
      },
      loadActiveProcessGroups() {
        let self = this;
        piincHttp.get('processes/groups', { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.activeProcessGroups = response.data
          if (self.activeProcessGroups && !self.activeProcessGroups.length) {
            self.activeProcessGroups.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
    },
    created () {
      if (this.getClient && this.getClient.slug) {
        this.loadContent();
        this.loadActiveProcessGroups()
      }
    }
  }
</script>
